import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DollarSign, Download } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { toast } from '../store/toastStore';
import DebtAgingPanel from '../components/billing/DebtAgingPanel';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import { NotLoadedValue } from '../components/ui/StateBlock';
import Kpi from '../components/ui/Kpi';
import DataTable from '../components/DataTable';
import type { ListSchema } from '../components/table/listParams';
import { useListParams } from '../hooks/useListParams';
import { monthKey, monthStart, recentMonths, sumBilling } from '../components/finance/financeModel';
import { billingColumns, billingFooter, billingHref } from '../components/finance/billingTable';
import { dateFormatter, formatCurrency, formatNumber } from '../utils/format';

/** `?month=2026-09` (absent = this month), so a link reproduces the month. */
const BILLING_PARAMS = {
  month: { kind: 'string', pattern: /^\d{4}-(0[1-9]|1[0-2])$/ },
} satisfies ListSchema;

/**
 * Customer billing for a month (T-41). The table is DataTable v2: a wide
 * finance table that scrolls on phones (every column kept), tabular numbers,
 * money with 2 decimals and the totals in the table footer. The month is in the URL.
 */
export default function BillingPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const { openedByOverride } = usePermissions();
  const list = useListParams(BILLING_PARAMS);
  const [today] = useState(() => new Date());
  const month = list.values.month || monthKey(today);
  const selectedMonth = monthStart(month);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['billing', selectedMonth],
    queryFn: () => api.getBilling({ month: selectedMonth }),
  });

  const billing = data?.data ?? [];
  // A failed load is never shown as ₪0 (DESIGN_SYSTEM.md §14, F-030).
  const failed = isError && !data;
  const totals = sumBilling(billing);

  const handleExport = async () => {
    const token = localStorage.getItem('auth_token');
    const url = api.getExportBillingUrl({ month: selectedMonth });
    try {
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        let message = t('billing.exportFailed');
        try {
          const body = await response.json();
          if (body?.error?.message) message = body.error.message;
        } catch {
          // non-JSON error body — keep the generic message
        }
        toast('error', message);
        return;
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `billing-${month}.csv`; // DQ-047: named by the exported month, not today's UTC date
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', t('billing.exportFailed'));
    }
  };

  const fmtMonth = dateFormatter({ month: 'long', year: 'numeric' }, i18n.language);
  const monthLabel = (m: string) => {
    const [y, mm] = m.split('-').map(Number);
    return fmtMonth.format(new Date(y, mm - 1, 1));
  };
  const months = recentMonths(today, 12);
  if (!months.includes(month)) months.push(month);

  const notLoaded = isLoading || failed;

  return (
    <div className="space-y-6">
      <PageHeader
        className="!mb-0"
        title={t('billing.title')}
        actions={
          // An export is a secondary action: the page's primary job is reading the month.
          <button type="button" onClick={handleExport} className="btn btn-secondary">
            <Download size={16} aria-hidden="true" />
            <span>{t('common.exportCsv')}</span>
          </button>
        }
      />

      {/* The period of everything below: one month. */}
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="billing-month" className="text-sm text-text-2">{t('uxFinance.billing.month')}</label>
        <select
          id="billing-month"
          value={month}
          onChange={(e) => list.set({ month: e.target.value === monthKey(today) ? '' : e.target.value })}
          className="input w-auto"
        >
          {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
      </div>

      {/* This month at a glance: what can be billed, for how many hours, from how many work logs. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        <Kpi
          className="col-span-2 md:col-span-1"
          label={t('billing.totalBillable')}
          value={notLoaded ? <NotLoadedValue /> : formatCurrency(totals.amount)}
          hint={failed ? t('states.notLoaded') : undefined}
        />
        <Kpi label={t('billing.billableHours')} value={notLoaded ? <NotLoadedValue /> : formatNumber(totals.billableHours, 1)} hint={failed ? t('states.notLoaded') : undefined} />
        <Kpi label={t('common.records')} value={notLoaded ? <NotLoadedValue /> : formatNumber(totals.logs)} hint={failed ? t('states.notLoaded') : undefined} />
      </div>

      <DataTable
        columns={billingColumns({ t })}
        data={billing}
        keyField="organization_id"
        caption={t('finance.billing.caption', { month: monthLabel(month) })}
        mobile="scroll"
        getRowHref={billingHref}
        rowLabel={(r) => r.organization_name}
        footer={billingFooter(totals, t)}
        isLoading={isLoading}
        skeletonRows={6}
        error={failed ? error : undefined}
        errorMessage={t('finance.billing.loadError')}
        onRetry={() => void refetch()}
        emptyState={<EmptyState icon={DollarSign} title={t('billing.noData')} description={t('uxFinance.billing.emptyHint')} />}
      />

      {/* What the two computed columns mean (A6), visible on every device: a tooltip would not reach touch. */}
      {!notLoaded && billing.length > 0 && (
        <dl aria-label={t('finance.billing.defsLabel')} className="-mt-3 space-y-1 text-xs text-text-2">
          <div><dt className="inline font-medium text-text">{t('finance.billing.defNonBillableTerm')}: </dt><dd className="inline">{t('finance.billing.defNonBillable')}</dd></div>
          <div><dt className="inline font-medium text-text">{t('finance.billing.defPerHourTerm')}: </dt><dd className="inline">{t('finance.billing.defPerHour')}</dd></div>
        </dl>
      )}

      {/* Receivables aging (decision 121) — rendered only with financial permission.
          /billing is a ManagerRoute (admin | customer_manager), so of the old
          "admin or agent" check only the admin half can ever be true. */}
      {/* FIX-PAGE-GRANT-FULL: a page opened to her (D-335 "view" / "edit") is opened whole; the server still checks money. */}
      {(isAdmin || openedByOverride('billing')) && <DebtAgingPanel />}
    </div>
  );
}
